import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import {
  FormControl,
  FormControlLabel,
  Grid,
  Input,
  InputLabel,
  Radio,
  RadioGroup,
} from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import classNames from 'classnames'
import * as R from 'ramda'
import {
  type AppointmentEventType,
  type Business,
  type Constant,
  EventTypeName,
  type Field,
  LanguageUtils,
  PuiSelect,
  PuiTextField,
  Text,
  TextWithTooltip,
  Utils,
} from '@pbt/pbt-ui-components'

import { ConversationTransport } from '~/api/graphql/generated/types'
import PuiSelectAll from '~/components/common/inputs/PuiSelectAll'
import PuiSwitch from '~/components/common/PuiSwitch'
import FeatureToggle from '~/constants/featureToggle'
import { getSmsCommunicationsEnabled } from '~/store/reducers/auth'
import {
  getAppointmentCommunicationInitializationType,
  getCommunicationTransportBackupOption,
  getFeatureToggle,
  getVisiblePolarisCommunicationTemplates,
} from '~/store/reducers/constants'
import {
  CommunicationTransportBackupOptionName,
  type DataHandle,
} from '~/types'
import { useBoopEnabled } from '~/utils/boop'
import { getCommunicationsMessageMaxLength } from '~/utils/communicationsUtils'
import { useEventType } from '~/utils/useEventType'

import MessageWithPreviewConfiguration from '../wellness-plans/MessageWithPreviewConfiguration'
import AutomaticCommunicationChangeInAppointmentStatusSettings from './AutomaticCommunicationChangeInAppointmentStatusSettings'
import AutomaticCommunicationSpecificTimeSettings from './AutomaticCommunicationSpecificTimeSettings'
import type { AutomaticCommunicationBaseProps } from './interfaces'
import { useAutomaticCommunicationFields } from './useAutomaticCommunicationFields'

const useStyles = makeStyles(
  (theme) => ({
    root: {},
    activeContainer: {
      borderBottom: theme.constants.tabBorder,
    },
    input: {
      width: 380,
    },
    radioLabel: {
      fontSize: '1.6rem',
      marginLeft: theme.spacing(0.5),
    },
    radio: {
      padding: theme.spacing(0.5),
    },
    labelRoot: {
      margin: theme.spacing(0, 2, 0, 0),
    },
  }),
  { name: 'AutomaticCommunication' },
)

const MESSAGE_MAX_LENGTH = getCommunicationsMessageMaxLength(
  ConversationTransport.Sms,
  true,
)

export interface AutomaticCommunicationProps
  extends AutomaticCommunicationBaseProps {
  business: Business
}

const AutomaticCommunication = forwardRef<
  DataHandle | undefined,
  AutomaticCommunicationProps
>(({ automaticCommunication, business, className }, ref) => {
  const classes = useStyles()
  const { t } = useTranslation(['Common', 'Businesses'])

  const PolarisCommunicationTemplates = useSelector(
    getVisiblePolarisCommunicationTemplates,
  )
  const CommunicationTransportBackupOption: Constant[] = useSelector(
    getCommunicationTransportBackupOption,
  )
  const InitializationType: Constant[] = useSelector(
    getAppointmentCommunicationInitializationType,
  )
  const isSmsCommunicationsEnabled: boolean = useSelector(
    getSmsCommunicationsEnabled,
  )

  const isBusinessOmniChannel = business.omniChannel

  const isAutomatedAppointmentNotificationsEnabled = useSelector(
    getFeatureToggle(FeatureToggle.CVC_AUTOMATED_APPOINTMENT_NOTIFICATIONS),
  )

  const shouldHideTextFields = isBusinessOmniChannel

  const AppointmentSubTypes: AppointmentEventType['subTypes'] = useEventType(
    EventTypeName.Appointment,
    'subTypes',
  )
  const AppointmentStatuses: AppointmentEventType['states'] = useEventType(
    EventTypeName.Appointment,
    'states',
  )
  const TextMessageMethodId: string = Utils.findConstantIdByName(
    CommunicationTransportBackupOptionName.TEXT_MESSAGE,
    CommunicationTransportBackupOption,
  )
  const BoopMessagedId: string = Utils.findConstantIdByName(
    CommunicationTransportBackupOptionName.BOOP_MESSAGE,
    CommunicationTransportBackupOption,
  )
  const SpecificTimeOptionId: string = Utils.findConstantIdByName(
    'At a specific time',
    InitializationType,
  )

  const boopFlowEnabled = useBoopEnabled(business)

  const {
    fields: {
      active,
      name,
      initializationTypeId,
      appointmentTypeIds,
      communicationMethodId,
      communicationMethodPrimaryBackupId,
      communicationMethodSecondaryBackupId,
      communicationMethodTertiaryBackupId,
      polarisCommunicationTemplateId,
      subject,
      template,
      textTemplate,
      appointmentStateIds,
    },
    validate,
    reset,
  } = useAutomaticCommunicationFields({
    automaticCommunication,
    shouldHideTextFields,
  })
  const shouldShowAppointmentStateIds =
    isAutomatedAppointmentNotificationsEnabled &&
    isBusinessOmniChannel &&
    initializationTypeId.value === SpecificTimeOptionId
  const shouldShowPolarisCommunicationTemplateId =
    isAutomatedAppointmentNotificationsEnabled && isBusinessOmniChannel

  const specificTimeSettingsRef = useRef<DataHandle>()
  const changeInAppointmentStatusSettingsRef = useRef<DataHandle>()

  const backupFields = [
    communicationMethodId,
    communicationMethodPrimaryBackupId,
    communicationMethodSecondaryBackupId,
    communicationMethodTertiaryBackupId,
  ]

  useEffect(() => {
    reset()
  }, [automaticCommunication])

  useEffect(() => {
    if (!communicationMethodPrimaryBackupId.value) {
      communicationMethodSecondaryBackupId.setValue('')
      communicationMethodTertiaryBackupId.setValue('')
    }
  }, [communicationMethodPrimaryBackupId.value])

  useEffect(() => {
    if (!communicationMethodSecondaryBackupId.value) {
      communicationMethodTertiaryBackupId.setValue('')
    }
  }, [communicationMethodSecondaryBackupId.value])

  const methodEnabledForBusiness = (methodId: string) => {
    if (methodId === TextMessageMethodId) {
      return isSmsCommunicationsEnabled
    }

    return true
  }

  useImperativeHandle(ref, () => ({
    validate: () =>
      validate() &&
      Boolean(specificTimeSettingsRef.current?.validate()) &&
      Boolean(changeInAppointmentStatusSettingsRef.current?.validate()),
    get: () => ({
      ...automaticCommunication,
      active: active.value,
      name: name.value,
      initializationTypeId: initializationTypeId.value,
      appointmentTypeIds: R.pluck<any, string>('id', appointmentTypeIds.value),
      appointmentStateIds: shouldShowAppointmentStateIds
        ? R.pluck<any, string>('id', appointmentStateIds.value)
        : [],
      polarisCommunicationTemplateId:
        polarisCommunicationTemplateId.value || null,
      communicationMethodId: communicationMethodId.value,
      communicationMethodPrimaryBackupId:
        communicationMethodPrimaryBackupId.value || null,
      communicationMethodSecondaryBackupId:
        communicationMethodSecondaryBackupId.value || null,
      communicationMethodTertiaryBackupId:
        communicationMethodTertiaryBackupId.value || null,
      subject: subject.value,
      template: template.value,
      textTemplate: textTemplate.value,
      ...specificTimeSettingsRef.current?.get(),
      ...changeInAppointmentStatusSettingsRef.current?.get(),
    }),
  }))

  const createBackupContactMethodList = (backupField: Field) => {
    const valuesToExclude = R.pipe(
      R.without([backupField]),
      R.pluck('value'),
    )(backupFields)

    return CommunicationTransportBackupOption.filter(
      (item) => boopFlowEnabled || item.id !== BoopMessagedId,
    ).map((item) => ({
      ...item,
      disabled:
        R.includes(item.id, valuesToExclude) ||
        !methodEnabledForBusiness(item.id),
    }))
  }

  return (
    <Grid
      container
      item
      className={classNames(className, classes.root)}
      direction="column"
    >
      <Grid item className={classes.activeContainer} px={3} py={0.5}>
        <PuiSwitch field={active} label={active.label} />
      </Grid>
      <Grid container item direction="column" px={3}>
        <Grid item mb={2}>
          <PuiTextField
            className={classes.input}
            field={name}
            inputProps={{ maxLength: 100 }}
            label={`${name.label}*`}
          />
        </Grid>
        <FormControl component="fieldset">
          <Text strong variant="subheading3">
            {t('Common:WHEN_TO_SEND')}
          </Text>
          <RadioGroup
            row
            aria-label="initializationTypeIdGroup"
            name="initializationTypeIdGroup1"
            value={initializationTypeId.value}
            onChange={(_, value) => {
              initializationTypeId.setValue(value)
            }}
          >
            {InitializationType.map((type) => (
              <FormControlLabel
                classes={{
                  root: classes.labelRoot,
                  label: classes.radioLabel,
                }}
                control={<Radio className={classes.radio} />}
                key={type.id}
                label={
                  SpecificTimeOptionId === type.id ? (
                    <TextWithTooltip
                      allowWrap
                      tooltipText={t(
                        'Businesses:APPOINTMENT_COMMUNICATIONS.COMMUNICATIONS_WILL_NOT_BE_SENT',
                      )}
                      variant="body"
                    >
                      {LanguageUtils.getTranslatedFieldName(type)}
                    </TextWithTooltip>
                  ) : (
                    LanguageUtils.getTranslatedFieldName(type)
                  )
                }
                value={type.id}
              />
            ))}
          </RadioGroup>
        </FormControl>
        <Grid item mt={2}>
          <AutomaticCommunicationSpecificTimeSettings
            automaticCommunication={automaticCommunication}
            initializationTypeId={initializationTypeId.value}
            ref={specificTimeSettingsRef}
          />
        </Grid>
        <Grid item mt={2}>
          <AutomaticCommunicationChangeInAppointmentStatusSettings
            automaticCommunication={automaticCommunication}
            initializationTypeId={initializationTypeId.value}
            ref={changeInAppointmentStatusSettingsRef}
          />
        </Grid>
        {shouldShowAppointmentStateIds && (
          <Grid item mt={2}>
            <PuiSelectAll
              field={appointmentStateIds}
              items={AppointmentStatuses}
              label={appointmentStateIds.label}
            />
          </Grid>
        )}
        <Grid item mb={3} mt={shouldShowAppointmentStateIds ? 0 : 2}>
          <PuiSelectAll
            field={appointmentTypeIds}
            items={AppointmentSubTypes}
            label={appointmentTypeIds.label}
          />
        </Grid>
        {shouldShowPolarisCommunicationTemplateId && (
          <>
            <Text strong variant="subheading3">
              {t('Businesses:APPOINTMENT_COMMUNICATIONS.TEMPLATE_SUBHEADING')}
            </Text>
            <Grid item mb={3}>
              <FormControl fullWidth margin="none">
                <InputLabel
                  className="ml-0"
                  htmlFor="communication-event-type-select"
                >
                  {polarisCommunicationTemplateId.label}
                </InputLabel>
                <PuiSelect
                  field={polarisCommunicationTemplateId}
                  input={<Input id="communication-event-type-select" />}
                  items={PolarisCommunicationTemplates}
                  renderEmpty={false}
                />
              </FormControl>
            </Grid>
          </>
        )}
        <TextWithTooltip
          allowWrap
          strong
          tooltipText={t(
            'Businesses:APPOINTMENT_COMMUNICATIONS.THIS_WILL_DETERMINE_ORDER',
          )}
          variant="subheading3"
        >
          {t('Common:SEND_AS')}
        </TextWithTooltip>
        <Grid container item columnSpacing={3} wrap="nowrap">
          <Grid item xs>
            <FormControl fullWidth margin="none">
              <InputLabel htmlFor="default-select">
                {communicationMethodId.label}
              </InputLabel>
              <PuiSelect
                field={communicationMethodId}
                input={<Input id="default-select" />}
                items={createBackupContactMethodList(communicationMethodId)}
                renderEmpty={false}
              />
            </FormControl>
          </Grid>
          <Grid item xs>
            <FormControl fullWidth margin="none">
              <InputLabel shrink htmlFor="primary-backup-select">
                {communicationMethodPrimaryBackupId.label}
              </InputLabel>
              <PuiSelect
                field={communicationMethodPrimaryBackupId}
                input={<Input id="primary-backup-select" />}
                items={createBackupContactMethodList(
                  communicationMethodPrimaryBackupId,
                )}
                placeholder={t('Common:NONE')}
              />
            </FormControl>
          </Grid>
          <Grid item xs>
            <FormControl fullWidth margin="none">
              <InputLabel shrink htmlFor="secondary-backup-select">
                {communicationMethodSecondaryBackupId.label}
              </InputLabel>
              <PuiSelect
                disabled={!communicationMethodPrimaryBackupId.value}
                field={communicationMethodSecondaryBackupId}
                input={<Input id="secondary-backup-select" />}
                items={createBackupContactMethodList(
                  communicationMethodSecondaryBackupId,
                )}
                placeholder={t('Common:NONE')}
              />
            </FormControl>
          </Grid>
          <Grid item xs>
            <FormControl fullWidth margin="none">
              <InputLabel shrink htmlFor="tertiary-backup-select">
                {communicationMethodTertiaryBackupId.label}
              </InputLabel>
              <PuiSelect
                disabled={
                  !communicationMethodPrimaryBackupId.value ||
                  !communicationMethodSecondaryBackupId.value
                }
                field={communicationMethodTertiaryBackupId}
                input={<Input id="tertiary-backup-select" />}
                items={createBackupContactMethodList(
                  communicationMethodTertiaryBackupId,
                )}
                placeholder={t('Common:NONE')}
              />
            </FormControl>
          </Grid>
        </Grid>
        <Grid item mt={2}>
          <MessageWithPreviewConfiguration
            field={subject}
            showButtons={false}
            showPreview={false}
            tooltipTitle={subject.label}
          />
        </Grid>
        {!shouldHideTextFields && (
          <>
            <Grid item mt={2}>
              <MessageWithPreviewConfiguration
                field={template}
                hidePlusButtonBlock={false}
                showPreview={false}
                tooltipTitle={template.label}
              />
            </Grid>
            <Grid item mt={2}>
              <MessageWithPreviewConfiguration
                field={textTemplate}
                hidePlusButtonBlock={false}
                maxLength={MESSAGE_MAX_LENGTH}
                showButtons={false}
                showPreview={false}
                tooltipTitle={textTemplate.label}
              />
            </Grid>
          </>
        )}
      </Grid>
    </Grid>
  )
})

export default AutomaticCommunication
